import { InstrumentReadings } from '../systems/Instruments';
import { NavReadings } from '../systems/Navigation';

/**
 * Canvas 2D drawing routines for the C172S "steam gauge" instruments.
 * Each function draws one instrument centred at (cx, cy) with radius r.
 */

type Ctx = CanvasRenderingContext2D;

function bezel(ctx: Ctx, cx: number, cy: number, r: number): void {
  ctx.save();
  // Outer ring
  ctx.beginPath();
  ctx.arc(cx, cy, r + 6, 0, Math.PI * 2);
  ctx.fillStyle = '#2b2b2e';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, r + 6, 0, Math.PI * 2);
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#111';
  ctx.stroke();
  // Face
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = '#0a0a0b';
  ctx.fill();
  ctx.restore();
}

function clipFace(ctx: Ctx, cx: number, cy: number, r: number): void {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
}

function needle(ctx: Ctx, cx: number, cy: number, angle: number, len: number, back: number, w: number, color: string): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(0, back);
  ctx.lineTo(0, -len);
  ctx.lineWidth = w;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.restore();
}

function label(ctx: Ctx, cx: number, cy: number, text: string, size: number, color = '#cfd2d6'): void {
  ctx.fillStyle = color;
  ctx.font = `${size}px ui-sans-serif, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, cx, cy);
}

// ---------------------------------------------------------------------------
export function drawAirspeed(ctx: Ctx, cx: number, cy: number, r: number, ias: number): void {
  bezel(ctx, cx, cy, r);
  // Scale 40..200 kt across 270 deg, gap at the TOP (12 o'clock): 40 sits just
  // clockwise of 12 (top-right) and 200 sits just counter-clockwise of it
  // (top-left) — matches the reference ASI photo, not the "gap at the bottom"
  // layout used by most six-pack instruments.
  const min = 40, max = 200;
  // toAng() is in "0 = up, clockwise +" convention (matching needle() below and
  // the -PI/2 conversions used elsewhere in this function for cos/sin) — so
  // +45deg (not -45) is what actually lands just clockwise of 12 o'clock.
  const a0 = Math.PI / 4; // start angle for `min`: top-right, just past 12 o'clock
  const sweep = Math.PI * 1.5;
  // Real ASIs space the scale by sqrt(v), not v: low speed (where precision
  // matters most — stall margin, approach) gets noticeably more angular room
  // than high speed, which is what makes 40-60 look so much wider than
  // 180-200 on the actual instrument.
  const sqrtMin = Math.sqrt(min), sqrtSpan = Math.sqrt(max) - sqrtMin;
  const toAng = (v: number) => a0 + ((Math.sqrt(v) - sqrtMin) / sqrtSpan) * sweep;

  // Colored arcs — real C172S V-speeds (see sim/constants.ts): white is the
  // flap-operating range (clamped to the scale's own minimum, since Vs0 sits
  // just below the bottom of the numbered scale), green is normal operation,
  // yellow is the caution range, red line marks Vne.
  const arc = (v1: number, v2: number, color: string, rr: number, lw: number) => {
    ctx.beginPath();
    ctx.arc(cx, cy, rr, toAng(v1) - Math.PI / 2, toAng(v2) - Math.PI / 2);
    ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.stroke();
  };
  arc(40, 85, '#e8e8e8', r - 10, 4);   // white (flap operating, Vs1..Vfe)
  arc(40, 129, '#2fbf4f', r - 4, 5);   // green (normal, Vs1..Vno)
  arc(129, 163, '#e0b62a', r - 4, 5);  // yellow (caution, Vno..Vne)

  // Redline tick at Vne
  ctx.strokeStyle = '#d22'; ctx.lineWidth = 2;
  const vneA = toAng(163);
  ctx.beginPath();
  ctx.moveTo(cx + Math.cos(vneA - Math.PI / 2) * (r - 12), cy + Math.sin(vneA - Math.PI / 2) * (r - 12));
  ctx.lineTo(cx + Math.cos(vneA - Math.PI / 2) * (r + 2), cy + Math.sin(vneA - Math.PI / 2) * (r + 2));
  ctx.stroke();

  // Ticks + numbers every 20
  ctx.strokeStyle = '#ddd';
  for (let v = min; v <= max; v += 10) {
    const a = toAng(v) - Math.PI / 2;
    const big = v % 20 === 0;
    const ri = r - (big ? 16 : 10);
    ctx.beginPath();
    ctx.lineWidth = big ? 2 : 1;
    ctx.moveTo(cx + Math.cos(a) * ri, cy + Math.sin(a) * ri);
    ctx.lineTo(cx + Math.cos(a) * (r - 4), cy + Math.sin(a) * (r - 4));
    ctx.stroke();
    if (big) label(ctx, cx + Math.cos(a) * (r - 30), cy + Math.sin(a) * (r - 30), String(v), r * 0.12);
  }
  label(ctx, cx, cy - r * 0.4, 'AIRSPEED', r * 0.1, '#8a8d92');
  label(ctx, cx, cy + r * 0.42, 'KNOTS', r * 0.09, '#8a8d92');

  const clamped = Math.min(max, Math.max(min, ias));
  needle(ctx, cx, cy, toAng(clamped), r - 12, r * 0.18, 3, '#f4f4f4');
  hub(ctx, cx, cy);
}

// ---------------------------------------------------------------------------
export function drawAttitude(ctx: Ctx, cx: number, cy: number, r: number, pitchDeg: number, rollDeg: number): void {
  bezel(ctx, cx, cy, r);
  ctx.save();
  clipFace(ctx, cx, cy, r);
  ctx.translate(cx, cy);
  ctx.rotate((-rollDeg * Math.PI) / 180);
  const pxPerDeg = r / 25;
  const horizon = pitchDeg * pxPerDeg;
  // Sky
  ctx.fillStyle = '#3d7fb5';
  ctx.fillRect(-r * 2, -r * 2, r * 4, r * 2 + horizon);
  // Ground
  ctx.fillStyle = '#6a4a2a';
  ctx.fillRect(-r * 2, horizon, r * 4, r * 2);
  // Horizon line
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(-r * 2, horizon); ctx.lineTo(r * 2, horizon); ctx.stroke();
  // Pitch ladder — black bars, longer at 20 deg than at 10 deg (no numbers,
  // matching the compact reference gauge's look).
  ctx.strokeStyle = '#111';
  for (let p = -20; p <= 20; p += 10) {
    if (p === 0) continue;
    const y = horizon - p * pxPerDeg;
    const w = Math.abs(p) === 20 ? r * 0.34 : r * 0.2;
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(-w, y); ctx.lineTo(w, y); ctx.stroke();
  }
  ctx.restore();

  // Bank scale (fixed): thin ticks at 10/20 deg, thick at 30/60/90.
  ctx.save();
  clipFace(ctx, cx, cy, r);
  ctx.strokeStyle = '#f0f0f0';
  for (const b of [-90, -60, -30, -20, -10, 10, 20, 30, 60, 90]) {
    const a = (-90 + b) * Math.PI / 180;
    const thick = Math.abs(b) === 30 || Math.abs(b) === 60 || Math.abs(b) === 90;
    ctx.lineWidth = thick ? 3.5 : 1.5;
    const ri = thick ? r - 16 : r - 10;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * ri, cy + Math.sin(a) * ri);
    ctx.lineTo(cx + Math.cos(a) * (r - 2), cy + Math.sin(a) * (r - 2));
    ctx.stroke();
  }
  // Fixed zero-bank reference (small white triangle, does not rotate)
  ctx.fillStyle = '#f0f0f0';
  ctx.beginPath();
  ctx.moveTo(cx, cy - r + 1); ctx.lineTo(cx - 5, cy - r + 11); ctx.lineTo(cx + 5, cy - r + 11); ctx.closePath();
  ctx.fill();

  // Roll pointer — orange, rotates with roll
  ctx.translate(cx, cy);
  ctx.rotate((-rollDeg * Math.PI) / 180);
  ctx.beginPath();
  ctx.moveTo(0, -r + 14); ctx.lineTo(-6, -r + 26); ctx.lineTo(6, -r + 26); ctx.closePath();
  ctx.fillStyle = '#e8791a'; ctx.fill();
  ctx.restore();

  // Fixed centre reference: orange dot + horizontal orange bars (wings/roll)
  ctx.save();
  ctx.strokeStyle = '#e8791a'; ctx.fillStyle = '#e8791a'; ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.4, cy); ctx.lineTo(cx - r * 0.12, cy);
  ctx.moveTo(cx + r * 0.12, cy); ctx.lineTo(cx + r * 0.4, cy);
  ctx.stroke();
  ctx.beginPath(); ctx.arc(cx, cy, r * 0.045, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

// ---------------------------------------------------------------------------
export function drawAltimeter(ctx: Ctx, cx: number, cy: number, r: number, altFt: number, setting: number): void {
  bezel(ctx, cx, cy, r);
  ctx.strokeStyle = '#ddd'; ctx.fillStyle = '#ddd';
  // 0..1000 around the dial (like a clock), numbers 0..9.
  for (let i = 0; i < 50; i++) {
    const a = (i / 50) * Math.PI * 2 - Math.PI / 2;
    const big = i % 5 === 0;
    const ri = r - (big ? 16 : 9);
    ctx.beginPath(); ctx.lineWidth = big ? 2 : 1;
    ctx.moveTo(cx + Math.cos(a) * ri, cy + Math.sin(a) * ri);
    ctx.lineTo(cx + Math.cos(a) * (r - 3), cy + Math.sin(a) * (r - 3));
    ctx.stroke();
  }
  ctx.font = `${r * 0.16}px sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let n = 0; n < 10; n++) {
    const a = (n / 10) * Math.PI * 2 - Math.PI / 2;
    ctx.fillText(String(n), cx + Math.cos(a) * (r - 32), cy + Math.sin(a) * (r - 32));
  }
  label(ctx, cx, cy - r * 0.34, 'ALTIMETER', r * 0.09, '#8a8d92');
  label(ctx, cx, cy - r * 0.15, 'CALIBRATED', r * 0.06, '#8a8d92');
  label(ctx, cx, cy - r * 0.08, 'TO 20,000FT', r * 0.06, '#8a8d92');

  // Dual pressure-setting readout: inHg (right) and the equivalent hPa (left)
  // — the same physical Kollsman setting in two units, both driven by the one
  // adjustment knob at the bottom-left of the instrument.
  const HPA_PER_INHG = 33.8639;
  const hPa = setting * HPA_PER_INHG;
  label(ctx, cx + r * 0.62, cy + r * 0.32, setting.toFixed(2), r * 0.09, '#9fe');
  label(ctx, cx + r * 0.62, cy + r * 0.44, 'inHg', r * 0.06, '#8a8d92');
  label(ctx, cx - r * 0.62, cy + r * 0.32, hPa.toFixed(0), r * 0.09, '#9fe');
  label(ctx, cx - r * 0.62, cy + r * 0.44, 'hPa', r * 0.06, '#8a8d92');

  // Single adjustment knob, bottom-left, that sets both readouts at once.
  const knobCx = cx - r * 0.86, knobCy = cy + r * 0.98, knobR = r * 0.22;
  ctx.beginPath(); ctx.arc(knobCx, knobCy, knobR, 0, Math.PI * 2);
  ctx.fillStyle = '#2c2c2e'; ctx.fill();
  ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.stroke();
  ctx.strokeStyle = '#888'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(knobCx, knobCy); ctx.lineTo(knobCx, knobCy - knobR * 0.75); ctx.stroke();

  // Pointers: 100ft (long), 1000ft (short), 10000ft (small w/ triangle)
  const a100 = ((altFt % 1000) / 1000) * Math.PI * 2;
  const a1000 = ((altFt % 10000) / 10000) * Math.PI * 2;
  const a10000 = ((altFt % 100000) / 100000) * Math.PI * 2;
  // 10000 ft thin pointer
  needle(ctx, cx, cy, a10000, r - 6, r * 0.1, 2, '#bbb');
  // 1000 ft short fat
  needle(ctx, cx, cy, a1000, r * 0.55, r * 0.12, 5, '#f0f0f0');
  // 100 ft long thin
  needle(ctx, cx, cy, a100, r - 10, r * 0.18, 3, '#f6f6f6');
  hub(ctx, cx, cy);
}

// ---------------------------------------------------------------------------
export function drawTurnCoordinator(ctx: Ctx, cx: number, cy: number, r: number, turnRate: number, slip: number): void {
  bezel(ctx, cx, cy, r);
  // A real turn coordinator's gyro senses roll + yaw rate, so the miniature
  // airplane's deflection represents TURN RATE, not literal bank angle — the
  // standard-rate index marks are a calibration reference: at exactly
  // standard rate (3 deg/s, a 2-minute turn) the wingtip lines up with the
  // mark. The marks sit at Math.PI/3 (60 deg off vertical / 30 deg off
  // horizontal, i.e. ~4 and 8 o'clock — see below), so the wing must rotate
  // that same 30 deg at turnRate=3 for the alignment to actually be true;
  // the previous 20 deg (0.35 rad) fell short of the marks.
  const bankShown = Math.max(-1.4, Math.min(1.4, turnRate / 3)) * (Math.PI / 6); // 30 deg at std rate
  label(ctx, cx, cy - r * 0.72, 'TURNCOORDINATOR', r * 0.08, '#8a8d92');

  // Airplane symbol
  ctx.save();
  ctx.translate(cx, cy - r * 0.05);
  ctx.rotate(bankShown);
  ctx.strokeStyle = '#f5d000'; ctx.fillStyle = '#f5d000'; ctx.lineWidth = 4;
  ctx.beginPath(); ctx.moveTo(-r * 0.7, 0); ctx.lineTo(r * 0.7, 0); ctx.stroke(); // wings
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -r * 0.28); ctx.stroke(); // tail
  ctx.beginPath(); ctx.arc(0, 0, r * 0.06, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // Inclinometer: a light silvery "tube" the ball rolls along, with two black
  // reference bars flanking the centred position.
  const troughY = cy + r * 0.66;
  const troughHalfW = r * 0.58;
  ctx.strokeStyle = '#8f9096'; ctx.lineWidth = r * 0.24; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(cx - troughHalfW, troughY); ctx.lineTo(cx + troughHalfW, troughY); ctx.stroke();
  ctx.strokeStyle = '#3a3b3f'; ctx.lineWidth = r * 0.03;
  ctx.beginPath(); ctx.moveTo(cx - troughHalfW, troughY - r * 0.12); ctx.lineTo(cx + troughHalfW, troughY - r * 0.12);
  ctx.moveTo(cx - troughHalfW, troughY + r * 0.12); ctx.lineTo(cx + troughHalfW, troughY + r * 0.12); ctx.stroke();

  // Black centre-reference bars (fixed — the ball should sit between these when level)
  const markGap = r * 0.2;
  ctx.strokeStyle = '#111'; ctx.lineWidth = r * 0.07;
  ctx.beginPath();
  ctx.moveTo(cx - markGap, troughY - r * 0.14); ctx.lineTo(cx - markGap, troughY + r * 0.14);
  ctx.moveTo(cx + markGap, troughY - r * 0.14); ctx.lineTo(cx + markGap, troughY + r * 0.14);
  ctx.stroke();

  // The ball itself: a round light-grey ball, free to roll ±slip.
  const ballCx = cx + Math.max(-1, Math.min(1, slip)) * (troughHalfW - r * 0.2);
  ctx.fillStyle = '#c7c8ce';
  ctx.beginPath(); ctx.arc(ballCx, troughY, r * 0.13, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = '#55565c'; ctx.lineWidth = 1.5; ctx.stroke();

  // Standard-rate index marks (L / R): thin bars at the 4 and 8 o'clock
  // positions (approx.) — where the wingtip of the miniature airplane sits
  // at a standard-rate (3 deg/s) turn. Drawn last so the trough/ball (which
  // sit just below) never paint over them.
  for (const s of [-1, 1]) {
    // s = -1 -> left mark (8 o'clock), s = +1 -> right mark (4 o'clock)
    const a = Math.PI / 2 - s * (Math.PI / 3);
    const rr1 = r * 0.62, rr2 = r * 0.82;
    ctx.save();
    ctx.translate(cx + Math.cos(a) * (rr1 + rr2) * 0.5, cy + Math.sin(a) * (rr1 + rr2) * 0.5);
    ctx.rotate(a - Math.PI / 2);
    ctx.strokeStyle = '#eee'; ctx.lineWidth = r * 0.05; ctx.lineCap = 'butt';
    ctx.beginPath(); ctx.moveTo(0, -(rr2 - rr1) / 2); ctx.lineTo(0, (rr2 - rr1) / 2); ctx.stroke();
    ctx.restore();
  }
  label(ctx, cx + Math.cos(Math.PI / 2 + Math.PI / 3) * r * 0.42, cy + Math.sin(Math.PI / 2 + Math.PI / 3) * r * 0.42, 'L', r * 0.14);
  label(ctx, cx + Math.cos(Math.PI / 2 - Math.PI / 3) * r * 0.42, cy + Math.sin(Math.PI / 2 - Math.PI / 3) * r * 0.42, 'R', r * 0.14);
}

// ---------------------------------------------------------------------------
export function drawHeading(ctx: Ctx, cx: number, cy: number, r: number, headingDeg: number, bugDeg: number): void {
  bezel(ctx, cx, cy, r);
  ctx.save();
  clipFace(ctx, cx, cy, r);
  ctx.translate(cx, cy);
  ctx.rotate((-headingDeg * Math.PI) / 180);
  ctx.strokeStyle = '#eee'; ctx.fillStyle = '#eee';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let d = 0; d < 360; d += 5) {
    const a = (d * Math.PI) / 180 - Math.PI / 2;
    const big = d % 10 === 0;
    const ri = r - (big ? 15 : 9);
    ctx.beginPath(); ctx.lineWidth = big ? 2 : 1;
    ctx.moveTo(Math.cos(a) * ri, Math.sin(a) * ri);
    ctx.lineTo(Math.cos(a) * (r - 3), Math.sin(a) * (r - 3));
    ctx.stroke();
    if (d % 30 === 0) {
      // N/3/6/E/12/15/S/... (cardinal letters at 0/90/180/270, tens-of-degrees
      // elsewhere) — matches the CDI's compass-rose labeling. This is a rigid
      // printed card, so the digits rotate WITH it, not against it: whatever
      // sits at the top (12 o'clock) reads normal/upright, 3 o'clock reads
      // rotated 90° right, 6 o'clock upside down, 9 o'clock rotated 90° left.
      // `d` is the label's own position on the (unrotated) card, so rotating
      // by `d` here composes with the outer -headingDeg card rotation to give
      // exactly that screen-position-dependent tilt.
      const txt = d === 0 ? 'N' : d === 90 ? 'E' : d === 180 ? 'S' : d === 270 ? 'W' : String(d / 10);
      ctx.save();
      ctx.translate(Math.cos(a) * (r - 30), Math.sin(a) * (r - 30));
      ctx.rotate((d * Math.PI) / 180);
      ctx.font = `${r * 0.14}px sans-serif`;
      ctx.fillText(txt, 0, 0);
      ctx.restore();
    }
  }
  // Heading bug
  const ba = (bugDeg * Math.PI) / 180 - Math.PI / 2;
  ctx.fillStyle = '#f5b000';
  ctx.beginPath();
  ctx.moveTo(Math.cos(ba) * (r - 2), Math.sin(ba) * (r - 2));
  ctx.lineTo(Math.cos(ba - 0.06) * (r - 14), Math.sin(ba - 0.06) * (r - 14));
  ctx.lineTo(Math.cos(ba + 0.06) * (r - 14), Math.sin(ba + 0.06) * (r - 14));
  ctx.closePath(); ctx.fill();
  ctx.restore();

  // Fixed aircraft symbol + lubber line
  ctx.strokeStyle = '#f5d000'; ctx.fillStyle = '#f5d000'; ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx, cy - r * 0.4); ctx.lineTo(cx, cy + r * 0.4);
  ctx.moveTo(cx - r * 0.28, cy); ctx.lineTo(cx + r * 0.28, cy);
  ctx.moveTo(cx - r * 0.14, cy + r * 0.28); ctx.lineTo(cx + r * 0.14, cy + r * 0.28);
  ctx.stroke();
  // Top lubber
  ctx.beginPath(); ctx.moveTo(cx, cy - r); ctx.lineTo(cx - 6, cy - r + 12); ctx.lineTo(cx + 6, cy - r + 12); ctx.closePath(); ctx.fill();
}

// ---------------------------------------------------------------------------
export function drawVSI(ctx: Ctx, cx: number, cy: number, r: number, vsi: number): void {
  bezel(ctx, cx, cy, r);
  // 0 at 9 o'clock (left), +2000 up, -2000 down. Scale spans ~330 deg.
  const max = 2000;
  const a0 = Math.PI; // 9 o'clock
  const half = Math.PI * 0.83; // sweep each way
  // + (not -): climb (positive vsi) must sweep toward the UP label (upper-right)
  // and descent toward DN (lower-right); the previous sign had them swapped.
  const toAng = (v: number) => a0 + (Math.max(-max, Math.min(max, v)) / max) * half;

  ctx.strokeStyle = '#ddd'; ctx.fillStyle = '#ddd';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let v = -max; v <= max; v += 500) {
    const ang = toAng(v);
    const ri = r - 16;
    ctx.beginPath(); ctx.lineWidth = 2;
    ctx.moveTo(cx + Math.cos(ang) * ri, cy + Math.sin(ang) * ri);
    ctx.lineTo(cx + Math.cos(ang) * (r - 3), cy + Math.sin(ang) * (r - 3));
    ctx.stroke();
    label(ctx, cx + Math.cos(ang) * (r - 30), cy + Math.sin(ang) * (r - 30), String(Math.abs(v / 100)), r * 0.12);
  }
  // minor ticks
  for (let v = -max; v <= max; v += 100) {
    if (v % 500 === 0) continue;
    const ang = toAng(v);
    ctx.beginPath(); ctx.lineWidth = 1;
    ctx.moveTo(cx + Math.cos(ang) * (r - 9), cy + Math.sin(ang) * (r - 9));
    ctx.lineTo(cx + Math.cos(ang) * (r - 3), cy + Math.sin(ang) * (r - 3));
    ctx.stroke();
  }
  label(ctx, cx + r * 0.34, cy - r * 0.12, 'UP', r * 0.09, '#8a8d92');
  label(ctx, cx + r * 0.34, cy + r * 0.12, 'DN', r * 0.09, '#8a8d92');
  label(ctx, cx, cy + r * 0.45, 'VERTICAL SPEED', r * 0.08, '#8a8d92');
  label(ctx, cx, cy + r * 0.58, '100 FT/MIN', r * 0.07, '#8a8d92');

  const ang = toAng(vsi);
  needle(ctx, cx, cy, ang + Math.PI / 2, r - 10, r * 0.1, 3, '#f4f4f4');
  hub(ctx, cx, cy);
}

// ---------------------------------------------------------------------------
export function drawCDI(ctx: Ctx, cx: number, cy: number, r: number, nav: NavReadings, obsDeg: number): void {
  bezel(ctx, cx, cy, r);
  // Compass card with OBS course at top.
  ctx.save();
  clipFace(ctx, cx, cy, r);
  ctx.translate(cx, cy);
  ctx.rotate((-obsDeg * Math.PI) / 180);
  ctx.strokeStyle = '#ccc'; ctx.fillStyle = '#ccc';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let d = 0; d < 360; d += 10) {
    const a = (d * Math.PI) / 180 - Math.PI / 2;
    const big = d % 30 === 0;
    const ri = r - (big ? 14 : 8);
    ctx.beginPath(); ctx.lineWidth = big ? 2 : 1;
    ctx.moveTo(Math.cos(a) * ri, Math.sin(a) * ri);
    ctx.lineTo(Math.cos(a) * (r - 3), Math.sin(a) * (r - 3));
    ctx.stroke();
    if (big) {
      ctx.save();
      ctx.translate(Math.cos(a) * (r - 30), Math.sin(a) * (r - 30));
      // Rigid printed card, same as drawHeading: digits rotate WITH the
      // card, so whatever sits at 12 o'clock is upright, 3 o'clock is
      // rotated 90° right, 6 o'clock upside down, 9 o'clock 90° left.
      ctx.rotate((d * Math.PI) / 180);
      ctx.font = `${r * 0.14}px sans-serif`;
      const txt = d === 0 ? 'N' : d === 90 ? 'E' : d === 180 ? 'S' : d === 270 ? 'W' : String(d / 10);
      ctx.fillText(txt, 0, 0);
      ctx.restore();
    }
  }
  ctx.restore();

  // Fixed dashed reference cross (the deviation-scale lubber lines) — both
  // horizontal and vertical, matching the RMI/HSI-style photo rather than
  // just a row of deflection dots.
  ctx.strokeStyle = '#eee'; ctx.lineWidth = 2;
  ctx.setLineDash([r * 0.09, r * 0.07]);
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.62, cy); ctx.lineTo(cx + r * 0.62, cy);
  ctx.moveTo(cx, cy - r * 0.62); ctx.lineTo(cx, cy + r * 0.62);
  ctx.stroke();
  ctx.setLineDash([]);

  // Deflection dots (2-dot full-scale reference)
  ctx.fillStyle = '#888';
  for (let i = -2; i <= 2; i++) {
    if (i === 0) continue;
    ctx.beginPath(); ctx.arc(cx + i * r * 0.22, cy, 3, 0, Math.PI * 2); ctx.fill();
  }

  // Vertical CDI needle (localizer) — deflect horizontally
  const locX = cx + Math.max(-2, Math.min(2, nav.locDots)) * r * 0.22;
  ctx.strokeStyle = nav.hasSignal ? '#2fbf4f' : '#555';
  ctx.lineWidth = 3;
  ctx.beginPath(); ctx.moveTo(locX, cy - r * 0.55); ctx.lineTo(locX, cy + r * 0.55); ctx.stroke();

  // Horizontal glideslope needle — deflect vertically. gsDots is + when above
  // the path, and "fly to the needle" means above-path must show the needle
  // BELOW centre (commanding a descent); + (not -) here is what makes that so.
  const gsY = cy + Math.max(-2, Math.min(2, nav.gsDots)) * r * 0.22;
  ctx.strokeStyle = nav.hasSignal ? '#2fbf4f' : '#555';
  ctx.beginPath(); ctx.moveTo(cx - r * 0.55, gsY); ctx.lineTo(cx + r * 0.55, gsY); ctx.stroke();

  // GS flag — shown when there's no usable glideslope signal, lower-left area
  if (!nav.hasSignal) label(ctx, cx - r * 0.4, cy - r * 0.08, 'GS', r * 0.1, '#e33');

  // TO/FROM flag (small red box, upper-right of centre)
  ctx.fillStyle = 'rgba(160,20,20,0.85)';
  ctx.fillRect(cx + r * 0.2, cy - r * 0.44, r * 0.28, r * 0.16);
  ctx.fillStyle = '#fff'; ctx.font = `${r * 0.1}px sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(nav.toFrom === 'OFF' ? '' : nav.toFrom, cx + r * 0.34, cy - r * 0.36);

  // Course pointer — fixed triangles top AND bottom (the reciprocal course
  // marker), not just the top one.
  ctx.fillStyle = '#f5d000';
  ctx.beginPath(); ctx.moveTo(cx, cy - r); ctx.lineTo(cx - 6, cy - r + 14); ctx.lineTo(cx + 6, cy - r + 14); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(cx, cy + r); ctx.lineTo(cx - 6, cy + r - 14); ctx.lineTo(cx + 6, cy + r - 14); ctx.closePath(); ctx.fill();

  // OBS knob, bottom-left of the instrument (outside the dial face) — the
  // single control that sets the course pointer above.
  const knobCx = cx - r * 0.86, knobCy = cy + r * 0.98, knobR = r * 0.24;
  ctx.beginPath(); ctx.arc(knobCx, knobCy, knobR, 0, Math.PI * 2);
  ctx.fillStyle = '#1a1a1c'; ctx.fill();
  ctx.strokeStyle = '#f5d000'; ctx.lineWidth = 2; ctx.stroke();
  label(ctx, knobCx, knobCy, 'OBS', r * 0.09, '#f5d000');
  // Kept within the tightened row-to-row gap now used elsewhere on the panel
  // (see Panel.ts's edgeGap) so it doesn't run into whatever sits in the row
  // below — the THR/MIX instrument, in the current layout.
  label(ctx, cx, cy + r * 1.12, `${Math.round(obsDeg)}°`, r * 0.11, '#9fb');
}

// ---------------------------------------------------------------------------
/** Flap lever: 4 notches (UP/10/20/FULL) with the Vfe speed limit placarded
 * alongside each — 110 KIAS for UP/10 deg, 85 KIAS for 20/FULL, matching the
 * real C172S flap-speed limitations. `detent` is an index 0..3. */
export function drawFlapLever(ctx: Ctx, x: number, y: number, w: number, h: number, detent: number): void {
  const rowH = h / 4;
  const notchLabels = ['UP', '10°', '20°', 'FULL'];

  // Speed-limit column (left): 110 spans UP/10 deg, 85 spans 20/FULL.
  const speedColW = w * 0.34;
  ctx.fillStyle = '#bcd4e8'; ctx.fillRect(x, y, speedColW, rowH * 2);
  ctx.fillStyle = '#e2e2e2'; ctx.fillRect(x, y + rowH * 2, speedColW, rowH * 2);
  ctx.strokeStyle = '#000'; ctx.strokeRect(x, y, speedColW, h);
  ctx.fillStyle = '#123'; ctx.font = `${rowH * 0.5}px sans-serif`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('110', x + speedColW / 2, y + rowH);
  ctx.fillText('85', x + speedColW / 2, y + rowH * 3);

  // Notch labels (right of the speed column)
  ctx.fillStyle = '#0c0c0d'; ctx.fillRect(x + speedColW, y, w - speedColW, h);
  ctx.strokeStyle = '#333'; ctx.strokeRect(x + speedColW, y, w - speedColW, h);
  ctx.fillStyle = '#ddd'; ctx.font = `${rowH * 0.4}px ui-monospace, monospace`; ctx.textAlign = 'left';
  notchLabels.forEach((t, i) => ctx.fillText(t, x + speedColW + 6, y + rowH * (i + 0.5)));
  for (let i = 1; i < 4; i++) {
    ctx.strokeStyle = '#333'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x, y + rowH * i); ctx.lineTo(x + w, y + rowH * i); ctx.stroke();
  }

  // Lever handle, positioned at the current detent's row, to the right of the gate.
  const handleY = y + rowH * (detent + 0.5);
  const handleX = x + w + w * 0.28;
  ctx.strokeStyle = '#777'; ctx.lineWidth = w * 0.1; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(handleX, handleY); ctx.lineTo(handleX, y + h + rowH * 0.5); ctx.stroke();
  ctx.fillStyle = '#c9c9ce';
  ctx.beginPath(); ctx.ellipse(handleX, handleY, w * 0.26, rowH * 0.34, 0, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = '#666'; ctx.lineWidth = 1; ctx.stroke();
}

// ---------------------------------------------------------------------------
export function drawTach(ctx: Ctx, cx: number, cy: number, r: number, rpm: number): void {
  bezel(ctx, cx, cy, r);
  const min = 0, max = 3500;
  const a0 = Math.PI * 0.75, sweep = Math.PI * 1.5;
  const toAng = (v: number) => a0 + ((v - min) / (max - min)) * sweep;
  // Green arc 2100..2700, red past 2700
  ctx.beginPath();
  ctx.arc(cx, cy, r - 6, toAng(2100) - Math.PI / 2, toAng(2700) - Math.PI / 2);
  ctx.strokeStyle = '#2fbf4f'; ctx.lineWidth = 5; ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, r - 6, toAng(2700) - Math.PI / 2, toAng(max) - Math.PI / 2);
  ctx.strokeStyle = '#d22'; ctx.lineWidth = 5; ctx.stroke();

  ctx.strokeStyle = '#ddd'; ctx.fillStyle = '#ddd';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let v = min; v <= max; v += 500) {
    const a = toAng(v) - Math.PI / 2;
    ctx.beginPath(); ctx.lineWidth = 2;
    ctx.moveTo(cx + Math.cos(a) * (r - 16), cy + Math.sin(a) * (r - 16));
    ctx.lineTo(cx + Math.cos(a) * (r - 4), cy + Math.sin(a) * (r - 4));
    ctx.stroke();
    label(ctx, cx + Math.cos(a) * (r - 30), cy + Math.sin(a) * (r - 30), String(v / 100), r * 0.11);
  }
  label(ctx, cx, cy - r * 0.36, 'RPM', r * 0.1, '#8a8d92');
  label(ctx, cx, cy + r * 0.36, 'x100', r * 0.09, '#8a8d92');
  needle(ctx, cx, cy, toAng(Math.max(min, Math.min(max, rpm))), r - 14, r * 0.16, 3, '#f4f4f4');
  hub(ctx, cx, cy);
}

function hub(ctx: Ctx, cx: number, cy: number): void {
  ctx.beginPath(); ctx.arc(cx, cy, 5, 0, Math.PI * 2);
  ctx.fillStyle = '#2a2a2a'; ctx.fill();
  ctx.strokeStyle = '#555'; ctx.lineWidth = 1; ctx.stroke();
}

export type { InstrumentReadings };
